/**
 * Site-wide bug / feature report dialog (CUSTOMER_FEEDBACK_PLAN Task 3.1).
 *
 * Wraps FeedbackForm in Modal with a bug/feature toggle. Auto-attaches the
 * context envelope (current route + appVersion) via FeedbackForm/buildFeedbackContext.
 * Focus moves into the dialog on open; Escape closes; focus returns to the trigger
 * (all handled by Modal.tsx).
 */

import { useEffect, useId, useState } from "react";
import { FeedbackForm, type FeedbackFormType } from "./FeedbackForm.js";
import { Modal } from "../ui/Modal.js";
import type { SurfaceId } from "../../lib/feedback/surfaces.js";

export interface ReportProblemDialogProps {
  open: boolean;
  onClose: () => void;
  /** Open in this mode (default "bug"). A ticket card (Task 6.1) can also open as "contact". */
  initialType?: "bug" | "feature" | "contact";
  /** Pre-fill the body (e.g. 404 broken-link reporter, or an assistant ticket draft). */
  prefillBody?: string;
  /** Override surface id (e.g. page.404, app.error-boundary). */
  surfaceId?: SurfaceId;
  /** Override route recorded in context (pathname; redacted by context builder). */
  route?: string;
  /** Extra allowlisted payload merged into context. */
  payload?: Record<string, unknown>;
  /** Called once, after a successful (including queued) submission, with the ticket ref. */
  onSubmitted?: (ref: string) => void;
}

function normalizeInitialType(initialType: "bug" | "feature" | "contact"): FeedbackFormType {
  return initialType === "feature" || initialType === "contact" ? initialType : "bug";
}

export function ReportProblemDialog({
  open,
  onClose,
  initialType = "bug",
  prefillBody = "",
  surfaceId,
  route,
  payload,
  onSubmitted,
}: ReportProblemDialogProps) {
  const titleId = useId();
  const [type, setType] = useState<FeedbackFormType>(normalizeInitialType(initialType));

  useEffect(() => {
    if (open) {
      setType(normalizeInitialType(initialType));
    }
  }, [open, initialType]);

  // When a surface is forced (404 / error boundary / an assistant card), hide
  // the bug/feature toggle — the caller has already decided the type.
  const lockedSurface = Boolean(surfaceId);
  const formType: FeedbackFormType = lockedSurface ? normalizeInitialType(initialType) : type;

  return (
    <Modal open={open} onClose={onClose} labelledBy={titleId}>
      <h2 id={titleId} className="text-lg font-semibold text-ink">
        Something wrong? Tell us.
      </h2>
      <p className="mt-1 text-sm text-muted">
        {lockedSurface
          ? "Describe what you expected. You will get a ticket reference."
          : "Report a bug or ask for a feature. You will get a ticket reference."}
      </p>

      {!lockedSurface ? (
        <div
          className="mt-4 inline-flex rounded-lg border border-line bg-slate-50 p-0.5"
          role="group"
          aria-label="Report type"
        >
          {(
            [
              { id: "bug", label: "Bug" },
              { id: "feature", label: "Feature" },
            ] as const
          ).map((opt) => {
            const selected = type === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                aria-pressed={selected}
                className={
                  "rounded-md px-3 py-1.5 text-sm transition " +
                  (selected
                    ? "bg-white font-medium text-ink shadow-sm"
                    : "text-muted hover:text-ink") +
                  " focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
                }
                onClick={() => setType(opt.id)}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      ) : null}

      <div className="mt-5">
        <FeedbackForm
          key={`${formType}-${open ? "open" : "closed"}`}
          type={formType}
          prefillBody={prefillBody}
          surfaceId={surfaceId}
          route={route}
          payload={payload}
          onSubmitted={onSubmitted}
        />
      </div>
    </Modal>
  );
}
